import React, { useMemo, useState } from 'react';
import { toast } from '../../../ui/toast';
import {
  ChevronDown,
  ChevronUp,
  ClipBoardIcon,
  FilterIcon,
  PencilIcon,
  SearchIcon,
  TrashIcon,
} from '../../../ui/icons/icons';
import { useDeleteCCAQuestionMutation, useGetStorageUrlQuery, useListCCAQuestionsQuery } from '../../../utils/api_service/ccaApi';
import { CCAQuestion } from '../../../utils/types/cca_types';
import {
  CCASubdomain,
  CCA_LEVEL_LABELS,
  CCA_SUBDOMAINS,
  LevelBadgeColors,
  PAGE_SIZE,
  SubdomainBadgeColors,
} from './ccaConstants';
import { CreateQuestionDialog } from './CreateQuestionDialog';

// ─── Delete confirmation dialog ──────────────────────────────────────────────

const DeleteConfirmDialog = ({
  question,
  isDeleting,
  onConfirm,
  onCancel,
}: {
  question: CCAQuestion;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-[2px]'>
    <div className='w-full max-w-sm rounded-xl bg-white shadow-xl'>
      <div className='flex items-start gap-3 p-5'>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100'>
          <TrashIcon className='h-5 w-5 text-rose-600' />
        </div>
        <div className='min-w-0'>
          <p className='text-sm font-semibold text-slate-900'>Delete question?</p>
          <p className='mt-1 text-xs text-slate-500 line-clamp-2'>
            &ldquo;{ question.question.text }&rdquo;
          </p>
          <p className='mt-2 text-xs text-rose-500'>This action cannot be undone.</p>
        </div>
      </div>
      <div className='flex justify-end gap-2 border-t border-slate-100 px-5 py-3'>
        <button
            type='button'
            onClick={onCancel}
            disabled={isDeleting}
            className='rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors'
        >
          Cancel
        </button>
        <button
            type='button'
            onClick={onConfirm}
            disabled={isDeleting}
            className='rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60 transition-colors'
        >
          { isDeleting ? 'Deleting…' : 'Delete' }
        </button>
      </div>
    </div>
  </div>
);

const QUESTION_TYPE_LABELS: Record<string, string> = {
  'multiple_choice': 'MC',
  'short_answer': 'SA',
};

const getSubdomainBadgeColor = (subdomain: string) => {
  // Try exact match first or map snake_case to Title Case
  let mapped = subdomain;
  if (subdomain.includes('_')) {
    mapped = subdomain
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return SubdomainBadgeColors[mapped as CCASubdomain] ?? 'bg-slate-100 text-slate-600';
};

const getLevelBadgeColor = (level: number) =>
  LevelBadgeColors[level] ?? 'bg-slate-100 text-slate-600';

/** Derive display format from whether the question has an imageKey */
const getFormat = (question: CCAQuestion): 'image' | 'wordy' =>
  question.question.imageKey ? 'image' : 'wordy';

const QuestionImage: React.FC<{ imageKey: string; className?: string }> = ({ imageKey, className = '' }) => {
  const { data, isLoading, isError } = useGetStorageUrlQuery(imageKey);

  if (isLoading) {
    return (
      <span className={`inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[10px] text-slate-400 ${className}`}>
        <svg className='h-3 w-3 animate-spin' viewBox='0 0 24 24' fill='none'>
          <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
          <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v8z' />
        </svg>
        Loading…
      </span>
    );
  }

  if (isError || !data?.url) {
    return (
      <span className={`inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500 ${className}`}>
        <svg viewBox='0 0 16 16' className='h-3 w-3 shrink-0' fill='none' stroke='currentColor' strokeWidth={1.5}>
          <rect x='2' y='2' width='12' height='12' rx='2' />
          <circle cx='5.5' cy='5.5' r='1.5' />
          <polyline points='2,11 5,8 8,10 11,7 14,10' />
        </svg>
        { imageKey.split('/').pop() }
      </span>
    );
  }

  return (
    <img
        src={data.url}
        alt='Question image'
        className={`max-h-40 rounded-lg object-contain ring-1 ring-slate-200 ${className}`}
    />
  );
};

const CCAQuestionRow = ({ question, onEdit, onDelete }: { question: CCAQuestion; onEdit: (q: CCAQuestion) => void; onDelete: (q: CCAQuestion) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const format = getFormat(question);

  return (
    <div className='w-full rounded-lg border border-slate-200 bg-white transition-colors'>
      <div
          className='flex cursor-pointer items-center gap-2 px-3 py-2'
          onClick={() => setIsOpen((p) => !p)}
      >
        <button
            type='button'
            className='shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100'
            onClick={(e) => { e.stopPropagation(); setIsOpen((p) => !p); }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          { isOpen ? <ChevronUp className='h-3.5 w-3.5' /> : <ChevronDown className='h-3.5 w-3.5' /> }
        </button>

        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-medium text-slate-800'>
            { question.question.text }
          </p>
          <div className='mt-1 flex flex-wrap items-center gap-1'>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${getSubdomainBadgeColor(question.subdomain)}`}>
              { question.subdomain.includes('_') ? question.subdomain.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : question.subdomain }
            </span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${getLevelBadgeColor(question.level)}`}>
              Lvl { question.level } — { CCA_LEVEL_LABELS[question.level] ?? 'Unknown' }
            </span>
            <span className='rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700'>
              { QUESTION_TYPE_LABELS[question.questionType] ?? question.questionType }
            </span>
            <span className='rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-500 capitalize'>
              { format }
            </span>
          </div>
          <div className='flex shrink-0 items-center gap-1' onClick={(e) => e.stopPropagation()}>
            <button
                type='button'
                onClick={() => onEdit(question)}
                className='rounded-md p-1 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-colors'
                aria-label='Edit question'
            >
              <PencilIcon className='h-3.5 w-3.5' />
            </button>
            <button
                type='button'
                onClick={() => onDelete(question)}
                className='rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors'
                aria-label='Delete question'
            >
              <TrashIcon className='h-3.5 w-3.5' />
            </button>
          </div>
        </div>
      </div>

      { isOpen && (
        <div className='border-t border-slate-100 px-4 py-3 space-y-3'>
          { question.question.imageKey && (
            <div>
              <p className='text-[11px] font-semibold uppercase tracking-wide text-slate-500'>Question Image</p>
              <div className='mt-1.5'>
                <QuestionImage imageKey={question.question.imageKey} />
              </div>
            </div>
          ) }
          { question.idealAnswer && (
            <div>
              <p className='text-[11px] font-semibold uppercase tracking-wide text-slate-500'>Ideal Answer</p>
              <p className='mt-0.5 text-sm text-slate-700'>{ question.idealAnswer }</p>
            </div>
          ) }
          { (question.questionType === 'multiple-choice' || question.questionType === 'multiple_choice') && question.options.length > 0 && (
            <div>
              <p className='text-[11px] font-semibold uppercase tracking-wide text-slate-500'>Choices</p>
              <div className='mt-1.5 space-y-1.5'>
                { question.options.map((opt) => (
                  <div
                      key={opt.id}
                      className={`rounded-lg px-3 py-2 text-xs font-medium ${
                        opt.isCorrect
                          ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
                          : 'bg-slate-50 text-slate-700'
                      }`}
                  >
                    <div className='flex items-center gap-1'>
                      { opt.isCorrect && <span className='text-emerald-600'>✓</span> }
                      <span>{ opt.text }</span>
                    </div>
                    { opt.imageKey && (
                      <div className='mt-1.5'>
                        <QuestionImage imageKey={opt.imageKey} />
                      </div>
                    ) }
                  </div>
                )) }
              </div>
            </div>
          ) }
        </div>
      ) }
    </div>
  );
};

export const CCABank: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedSubdomain, setSelectedSubdomain] = useState<CCASubdomain | 'All'>('All');
  const [selectedLevel, setSelectedLevel] = useState<number | 'All'>('All');
  const [selectedType, setSelectedType] = useState<string | 'All'>('All');
  const [page, setPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<CCAQuestion | null>(null);
  const [deletingQuestion, setDeletingQuestion] = useState<CCAQuestion | null>(null);

  const [deleteQuestion, { isLoading: isDeleting }] = useDeleteCCAQuestionMutation();

  const handleEdit = (q: CCAQuestion) => {
    setEditingQuestion(q);
    setIsCreateOpen(true);
  };

  const handleDelete = (q: CCAQuestion) => {
    setDeletingQuestion(q);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingQuestion) return;
    try {
      await deleteQuestion(deletingQuestion.id).unwrap();
      toast.success('Question deleted.', { position: 'bottom-center' });
      setDeletingQuestion(null);
    } catch {
      toast.error('Failed to delete question.', { position: 'bottom-center' });
    }
  };

  const handleDialogClose = () => {
    setIsCreateOpen(false);
    setEditingQuestion(null);
  };

  const formatSubdomain = (sd: CCASubdomain | 'All'): string | undefined => {
    if (sd === 'All') return undefined;
    return sd.toLowerCase().replace(/ /g, '_');
  };

  const formatQuestionType = (qt: string | 'All'): string | undefined => {
    if (qt === 'All') return undefined;
    return qt.replace(/-/g, '_');
  };

  // Server-side filtering via API params
  const { data, isLoading, isError } = useListCCAQuestionsQuery({
    subdomain: formatSubdomain(selectedSubdomain),
    level: selectedLevel !== 'All' ? selectedLevel : undefined,
    question_type: formatQuestionType(selectedType),
    page,
    page_size: PAGE_SIZE,
  });

  // Client-side search on the current page's results
  const visibleQuestions = useMemo(() => {
    if (!data?.items) return [];
    if (!searchText.trim()) return data.items;
    const lower = searchText.toLowerCase();
    return data.items.filter((q) =>
      q.question.text.toLowerCase().includes(lower),
    );
  }, [data, searchText]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const hasFilters =
    searchText || selectedSubdomain !== 'All' || selectedLevel !== 'All' || selectedType !== 'All';

  const handleFilterChange = () => setPage(1);

  const selectClass =
    'rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300';

  return (
    <div className='px-4 py-4'>
      { /* Header */ }
      <div className='mb-3 flex items-center gap-3'>
        <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-600'>
          <ClipBoardIcon className='h-5 w-5' />
        </div>
        <div className='min-w-0'>
          <h1 className='text-lg font-bold text-slate-900 leading-tight'>CCA Question Bank</h1>
          <p className='text-xs text-slate-500'>
            Cognitive Capability Assessment — 5 subdomains × 10 levels × 50 questions per category
          </p>
        </div>
        <div className='ml-auto flex items-center gap-2 shrink-0'>
          { CCA_SUBDOMAINS.map((sd) => (
            <span
                key={sd}
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SubdomainBadgeColors[sd]}`}
            >
              { sd.split(' ')[0] }
            </span>
          )) }
          <button
              onClick={() => { setEditingQuestion(null); setIsCreateOpen(true); }}
              className='ml-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 transition-colors'
          >
            + Add Question
          </button>
        </div>
      </div>

      { /* Stats bar */ }
      <div className='mb-3 flex gap-4 rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-2.5'>
        { [
          { value: isLoading ? '—' : total.toLocaleString(), label: 'Total Questions' },
          { value: '5', label: 'Subdomains' },
          { value: '10', label: 'Levels' },
          { value: '50', label: 'Per Category/Level' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.label}>
            <div className='text-center'>
              <p className='text-base font-bold text-slate-800'>{ s.value }</p>
              <p className='text-[11px] text-slate-500'>{ s.label }</p>
            </div>
            { i < arr.length - 1 && <div className='w-px bg-slate-200' /> }
          </React.Fragment>
        )) }
        <div className='ml-auto flex items-center gap-1.5 text-sm text-slate-500'>
          <FilterIcon className='h-4 w-4' />
          <span className='font-semibold text-slate-700'>{ visibleQuestions.length }</span> showing
        </div>
      </div>

      { /* Filters */ }
      <div className='mb-3 flex flex-wrap items-center gap-2'>
        <div className='relative flex-1 min-w-[180px]'>
          <SearchIcon className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
          <input
              type='text'
              placeholder='Search questions...'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className='w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300'
          />
        </div>

        <select
            value={selectedSubdomain}
            onChange={(e) => {
              setSelectedSubdomain(e.target.value as CCASubdomain | 'All');
              handleFilterChange();
            }}
            className={selectClass}
        >
          <option value='All'>All Subdomains</option>
          { CCA_SUBDOMAINS.map((sd) => (
            <option key={sd} value={sd}>{ sd }</option>
          )) }
        </select>

        <select
            value={selectedLevel}
            onChange={(e) => {
              setSelectedLevel(e.target.value === 'All' ? 'All' : Number(e.target.value));
              handleFilterChange();
            }}
            className={selectClass}
        >
          <option value='All'>All Levels</option>
          { Array.from({ length: 10 }, (_, i) => i + 1).map((l) => (
            <option key={l} value={l}>Level { l } — { CCA_LEVEL_LABELS[l] }</option>
          )) }
        </select>

        <select
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              handleFilterChange();
            }}
            className={selectClass}
        >
          <option value='All'>All Types</option>
          <option value='multiple-choice'>Multiple Choice</option>
          <option value='short-answer'>Short Answer</option>
        </select>

        { hasFilters && (
          <button
              onClick={() => {
                setSearchText('');
                setSelectedSubdomain('All');
                setSelectedLevel('All');
                setSelectedType('All');
                setPage(1);
              }}
              className='rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 transition-colors'
          >
            Clear
          </button>
        ) }
      </div>

      { /* Loading / Error */ }
      { isLoading && (
        <div className='space-y-1'>
          { Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className='h-12 w-full animate-pulse rounded-lg bg-slate-100' />
          )) }
        </div>
      ) }

      { isError && (
        <div className='flex flex-col items-center justify-center py-12 text-rose-400'>
          <ClipBoardIcon className='mb-2 h-8 w-8' />
          <p className='text-sm font-medium'>Failed to load questions. Please try again.</p>
        </div>
      ) }

      { /* Question list */ }
      { !isLoading && !isError && (
        <>
          { visibleQuestions.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 text-slate-400'>
              <ClipBoardIcon className='mb-2 h-8 w-8' />
              <p className='text-sm font-medium'>No questions match your filters</p>
            </div>
          ) : (
            <div className='space-y-1'>
              { visibleQuestions.map((q) => (
                <CCAQuestionRow key={q.id} question={q} onEdit={handleEdit} onDelete={handleDelete} />
              )) }
            </div>
          ) }

          { /* Pagination */ }
          { totalPages > 1 && (
            <div className='mt-3 flex items-center justify-between'>
              <p className='text-xs text-slate-500'>
                Page { page } of { totalPages } ({ total.toLocaleString() } total)
              </p>
              <div className='flex items-center gap-1'>
                <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className='flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed'
                >
                  <ChevronDown className='h-3.5 w-3.5 rotate-90' />
                </button>

                { Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | '...')[]>((acc, p, i, arr) => {
                    if (i > 0 && typeof arr[i - 1] === 'number' && (p as number) - (arr[i - 1] as number) > 1)
                      acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className='px-1 text-xs text-slate-400'>…</span>
                    ) : (
                      <button
                          key={p}
                          onClick={() => setPage(p as number)}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-semibold transition-colors ${
                            page === p
                              ? 'bg-emerald-600 text-white shadow-sm'
                              : 'border border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                      >
                        { p }
                      </button>
                    ),
                  ) }

                <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className='flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed'
                >
                  <ChevronDown className='h-3.5 w-3.5 -rotate-90' />
                </button>
              </div>
            </div>
          ) }
        </>
      ) }

      <CreateQuestionDialog isOpen={isCreateOpen} onClose={handleDialogClose} question={editingQuestion ?? undefined} />

      { deletingQuestion && (
        <DeleteConfirmDialog
            question={deletingQuestion}
            isDeleting={isDeleting}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeletingQuestion(null)}
        />
      ) }
    </div>
  );
};

export default CCABank;
